import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { util as sdkUtil } from '../../../../../../util/sdkLoader';

import {
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  Modal,
  PrimaryButton,
  SecondaryButton,
} from '../../../../../../components';
import IconCard from '../../../../../../components/SavedCardDetails/IconCard/IconCard';

import css from './LanguageMenu.module.css';

// Import translation files
import enTranslations from '../../../../../../translations/en.json';
import nlTranslations from '../../../../../../translations/nl.json';
import { FormattedMessage, useIntl } from 'react-intl';

const LanguageMenu = ({ currentPage, currentUser, onUpdateProfile, onManageDisableScrolling }) => {
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const [currentLanguage, setCurrentLanguage] = useState('nl');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState(null);

  useEffect(() => {
    // Get saved language from localStorage
    let savedLanguage = localStorage.getItem('selectedLanguage');

    if (!savedLanguage) {
      savedLanguage = 'nl';
      localStorage.setItem('selectedLanguage', 'nl');

      // Update the translations dynamically
      window.__LANGUAGE_CHANGE__ = {
        locale: 'nl-NL',
        messages: nlTranslations
      };

      // Set LTR direction for Dutch
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', 'nl');

      // Notify app to update IntlProvider (no reload)
      try {
        window.dispatchEvent(
          new CustomEvent('languageChange', { detail: { locale: 'nl' } })
        );
      } catch (_) { }
    }

    setCurrentLanguage(savedLanguage);
    // Listen for global languageChange (footer or other triggers)
    const onLanguageChange = e => {
      const next = e?.detail?.locale === 'nl' ? 'nl' : 'en';
      setCurrentLanguage(next);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('languageChange', onLanguageChange);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('languageChange', onLanguageChange);
      }
    };
  }, []);

  const applyLanguageChange = (language) => {
    // Save language preference to localStorage
    localStorage.setItem('selectedLanguage', language);
    setCurrentLanguage(language);

    // Update the translations dynamically
    const translations = language === 'nl' ? nlTranslations : enTranslations;

    // Store translations in window for app.js to pick up
    window.__LANGUAGE_CHANGE__ = {
      locale: language === 'nl' ? 'nl-NL' : 'en-US',
      messages: translations
    };

    // Keep user on the same page and only change locale & direction
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    // English landing now lives at '/en' with route name 'EnglishLandingPage'
    const isOnEnglishLanding = location.pathname === '/en-home' || currentPage === 'EnglishLandingPage';
    // Dutch landing now lives at '/' (plus legacy '/p/nl-home' CMS page)
    const isOnDutchLanding =
      location.pathname === '/' ||
      location.pathname === '/p/nl-home' ||
      currentPage === 'LandingPage' ||
      currentPage === 'CMSPage:nl-home';

    if (language === 'nl') {
      // Navigate to Dutch landing page (route exists as '/nl-home')
      // Add RTL direction for Dutch
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', 'nl');
      // If currently on English LandingPage, redirect to Dutch landing page
      if (isOnEnglishLanding) {
        history.replace('/');
      } else {
        history.replace(currentPath);
      }

    } else {
      // Navigate to English home page
      // history.push('/');
      // Set LTR direction for English
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', 'en');
      // If currently on Dutch Landing, redirect to English landing page
      if (isOnDutchLanding) {
        history.replace('/en-home');
      } else {
        history.replace(currentPath);
      }
    }
    // Notify app to update IntlProvider (no reload)
    try {
      window.dispatchEvent(
        new CustomEvent('languageChange', { detail: { locale: language === 'nl' ? 'nl' : 'en' } })
      );
    } catch (_) { }
    // No full reload; avoid hydration mismatches/duplicates in production
  };

  const handleLanguageChange = (language) => {
    if (currentUser) {
      setPendingLanguage(language);
      setIsModalOpen(true);
    } else {
      applyLanguageChange(language);
    }
  };

  const handleConfirm = (shouldUpdateProfileWithSelection) => {
    if (shouldUpdateProfileWithSelection && onUpdateProfile) {
      onUpdateProfile({
        data: {
          publicData: {
            languagePreference: pendingLanguage
          }
        },
        options: {
          expand: true,
          include: ['profileImage'],
          'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
          'fields.image': [
            'variants.square-small',
            'variants.square-small2x',
            'variants.square-xsmall',
            'variants.square-xsmall2x',
          ],
          'imageVariant.square-xsmall': sdkUtil.objectQueryString({
            w: 40,
            h: 40,
            fit: 'crop',
          }),
          'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
            w: 80,
            h: 80,
            fit: 'crop',
          }),
        }
      });
    }

    // Always change the UI language to what was selected
    applyLanguageChange(pendingLanguage);
    setIsModalOpen(false);
  };

  const displayLanguageMessageId =
    currentLanguage === 'nl'
      ? 'TopbarDesktop.languageDutchLabel'
      : 'TopbarDesktop.languageEnglishLabel';

  return (
    <>
      <Menu>
        <MenuLabel className={css.languageMenuLabel} isOpenClassName={css.languageMenuIsOpen}>
          <div className={css.languageSelector}>
            {currentLanguage === 'en' ?
              <IconCard brand="flagEn" /> : <IconCard brand="flagNl" />}

            <span className={css.languageSelectorText}>
              <FormattedMessage id={displayLanguageMessageId} />
            </span>
            <IconCard brand='dropdownarrow' />
          </div>
        </MenuLabel>
        <MenuContent className={css.languageMenuContent}>

          <MenuItem key="english">
            <button
              className={css.languageOption}
              onClick={() => handleLanguageChange('en')}
              disabled={currentLanguage === 'en'}
            >
              <span className={css.menuItemBorder} />
              <IconCard brand='flagEn' />
              <FormattedMessage id="TopbarDesktop.languageEnglish" />
            </button>
          </MenuItem>
          <MenuItem key="dutch">
            <button
              className={css.languageOption}
              onClick={() => handleLanguageChange('nl')}
              disabled={currentLanguage === 'nl'}
            >
              <span className={css.menuItemBorder} />
              <IconCard brand='flagNl' />
              <FormattedMessage id="TopbarDesktop.languageDutch" />
            </button>
          </MenuItem>
        </MenuContent>
      </Menu>
      <Modal
        id="LanguagePreferenceModal"
        isOpen={isModalOpen}
        onClose={() => { handleConfirm(false); setIsModalOpen(false); }}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.modalContainer}>
          <p className={css.modalTitle}>
            <FormattedMessage id="LanguagePreferenceModal.title" />
          </p>
          <div className={css.modalActions}>
            <SecondaryButton className={css.modalButton} onClick={() => handleConfirm(false)}>
              <FormattedMessage id="LanguagePreferenceModal.no" />
            </SecondaryButton>
            <PrimaryButton className={css.modalButton} onClick={() => handleConfirm(true)}>
              <FormattedMessage id="LanguagePreferenceModal.yes" />
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default LanguageMenu;
